import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useCreateMainSiteContent, useUpdateMainSiteContent } from "../../../../api/main-site-content/main-site-content.hooks";
import { useContentPage } from '../context/ContentPageContext';
import Section from './Section';
import { standardizePageName } from '../../../../utils/stringUtils';
import { useUploadFile } from '../../../../api/fileUpload/fileUpload.hooks';
import CategorySelector from './CategorySelector';
import FileUploadSection from './FileUploadSection';
import MetaSection from '../../TopAttractions/_components/BySiteComponents/MetaSection';

const ContentPageBuilder = ({ editingContent, onBack, refetch }) => {
  const { sections, addSection, setSections, initializeWithHero, updateHeroSection } = useContentPage();
  const { createContent, isLoading: isCreating } = useCreateMainSiteContent();
  const { updateContent, isLoading: isUpdating } = useUpdateMainSiteContent();
  const { upload, isLoading: isUploading } = useUploadFile();
  const [pageName, setPageName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [meta, setMeta] = useState({ title: '', description: '' });
  const [validationErrors, setValidationErrors] = useState({});
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'info',
  });

  const showAlert = (message, variant = 'info') => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: '', variant: 'info' }),
      5000
    );
  };

  useEffect(() => {
    if (editingContent) {
      setPageName(editingContent.pageName || '');
      setSelectedCategories(editingContent.category || []);
      
      // Load meta from content array
      const metaFromContent = editingContent.content?.find(item => item.id === 'meta');
      setMeta({
        title: metaFromContent?.title || '',
        description: metaFromContent?.description || '',
      });
      
      if (editingContent.content && editingContent.content.length > 0) {
        // Filter out meta from sections
        const sectionsWithoutMeta = editingContent.content.filter(item => item.id !== 'meta');
        // Ensure hero section data is preserved
        const contentWithHero = initializeWithHero(sectionsWithoutMeta);
        const existingHero = sectionsWithoutMeta.find(section => section.isHero);
        
        if (existingHero) {
          // If existing content has hero data, preserve it
          const updatedContent = contentWithHero.map(section => 
            section.isHero ? existingHero : section
          );
          setSections(updatedContent);
        } else {
          setSections(contentWithHero);
        }
      } else {
        setSections(initializeWithHero([]));
      }
    } else {
      // New content page - initialize with hero section
      setSections(initializeWithHero([]));
      setMeta({ title: '', description: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContent, setSections, initializeWithHero]);

  const validateForm = () => {
    const errors = {};
    if (!pageName.trim()) {
      errors.pageName = 'Page name is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showAlert('Please fill in all required fields.', 'danger');
      return;
    }

    const pageData = {
      siteName: 'main-site',
      pageName: standardizePageName(pageName),
      type: 'content-page',
      content: [
        ...sections,
        { id: 'meta', title: meta.title, description: meta.description },
      ],
      category: selectedCategories,
    };

    try {
      let success = false;
      if (editingContent) {
        success = await updateContent(editingContent._id, pageData);
      } else {
        success = await createContent(pageData);
      }

      if (success) {
        showAlert(
          editingContent ? 'Updated successfully!' : 'Added successfully!',
          'success'
        );
        await refetch();
        setTimeout(() => {
          onBack();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to save page:', error);
      showAlert('Operation failed.', 'danger');
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { url } = await upload(file);
      if (url) {
        updateHeroSection({ image: url });
        showAlert('Hero image uploaded successfully!', 'success');
      }
    } catch (error) {
      console.error('Failed to upload hero image:', error);
      showAlert('Failed to upload hero image.', 'danger');
    }
  };

  const handleCategoryChange = useCallback((categories) => {
    setSelectedCategories(categories);
  }, []);

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: '', variant: 'info' })
          }
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      <div className="card p-4">
        <div className="d-flex align-items-center mb-4">
          <Button
            variant="link"
            className="p-0 me-3"
            onClick={onBack}
            type="button"
          >
            ← Back
          </Button>
          <h4 className="m-0">
            {editingContent ? 'Edit Content Page' : 'Add New Content Page'}
          </h4>
        </div>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Page Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={pageName}
              onChange={(e) => {
                setPageName(e.target.value);
                if (validationErrors.pageName) {
                  setValidationErrors((prev) => ({
                    ...prev,
                    pageName: undefined,
                  }));
                }
              }}
              isInvalid={!!validationErrors.pageName}
            />
            {pageName && (
              <div className="form-text">
                URL: /content-page/{standardizePageName(pageName)}
              </div>
            )}
            {validationErrors.pageName && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.pageName}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <CategorySelector
            onSelectionChange={handleCategoryChange}
            initialSelections={editingContent?.category || []}
          />

          {/* File Upload Section */}
          <FileUploadSection />

          {sections.length === 0 ? (
            <div className="card">
              <div className="card-body text-center" style={{ padding: '60px 20px' }}>
                <i className="bi bi-layout-text-window-reverse" style={{ fontSize: '4rem', color: '#BCBCBC' }}></i>
                <h5 className="mt-4" style={{ color: '#6c757d' }}>No sections yet</h5>
                <p className="text-muted">Click "Add Section" to start building your page</p>
              </div>
            </div>
          ) : (
            <>
              {sections.map((section) => (
                section.isHero ? (
                  <div key={section.id} className="card mb-3">
                    <div className="card-header bg-neutral-gray text-dark d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="bi bi-star-fill me-2"></i>
                        Hero Section
                      </h5>
                      <small className="badge bg-primary text-white">Required</small>
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-3">
                        <Form.Label>Hero Image</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleHeroImageUpload}
                          disabled={isUploading}
                        />
                        {isUploading && (
                          <div className="mt-2">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Uploading image...</span>
                          </div>
                        )}
                        {section.data.image && (
                          <div className="mt-2">
                            <img 
                              src={section.data.image} 
                              alt="Hero preview" 
                              style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                              className="img-thumbnail"
                            />
                            <Form.Control
                              type="text"
                              value={section.data.image}
                              onChange={(e) => updateHeroSection({ image: e.target.value })}
                              placeholder="Or enter image URL manually"
                              className="mt-2"
                            />
                          </div>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={section.data.title || ''}
                          onChange={(e) => updateHeroSection({ title: e.target.value })}
                          placeholder="Enter hero title"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Subtitle</Form.Label>
                        <Form.Control
                          type="text"
                          value={section.data.subtitle || ''}
                          onChange={(e) => updateHeroSection({ subtitle: e.target.value })}
                          placeholder="Enter hero subtitle"
                        />
                      </Form.Group>
                    </div>
                  </div>
                ) : (
                  <Section key={section.id} section={section} />
                )
              ))}
            </>
          )}

          <div className="mb-3">
            <Button
              variant="primary"
              onClick={addSection}
              type="button"
            >
              <i className="bi bi-plus-circle me-1"></i> Add Section
            </Button>
          </div>

          {/* Meta Section */}
          <MetaSection
            data={meta}
            onUpdate={setMeta}
          />

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              variant="secondary"
              onClick={onBack}
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isUpdating || isCreating}
              type="button"
            >
              {isUpdating || isCreating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingContent ? 'Saving...' : 'Creating...'}
                </>
              ) : editingContent ? (
                'Save Changes'
              ) : (
                'Save Content'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ContentPageBuilder;
